import { action, thunk } from 'easy-peasy';
import i18next from 'i18next';

import session from '@model/session';
import showcaseTable from '@model/showcase-table';

import { GTM } from '@app/gtm';
import Sentry from '@app/sentry';
import { ModelCustomer } from '@model/Customer';
import { ModelUser } from '@model/User';
import { JOB_PAGE_STATES } from '@model/Job/_constants/Job.constants';
import { utilityChangeLocale } from '@utils/locale';
import { jobStore } from '@model/Job/Job';
import { candidatesStore } from '@model/Candidate/Candidate';
import { contractStore } from '@model/Contract/Contract';
import { dealEntityStore } from '@model/DealEntity/DealEntity';

export default {
  // global state
  initialized: false,
  currentRoute: null,
  currentStatus: JOB_PAGE_STATES.DOING,
  authenticationError: null,
  appError: null,
  modalId: null,
  isModalOpened: false,

  initialize: thunk(
    async (
      actions,
      { isShareModalOpen, jobId, contractId },
      { getStoreActions, getStoreState },
    ) => {
      try {
        const {
          contract: { getContract },
          session: { authenticate },
          user: { fetchUser },
          showcaseTable: { initTable },
          job: {
            getJob,
            clients: { getClients },
          },
          candidates: { getCandidates },
        } = getStoreActions();

        await utilityChangeLocale();

        if (isShareModalOpen) {
          actions.setModalId('share');
          actions.setModalOpened(true);
        }

        if (!jobId && !contractId) {
          try {
            return await authenticate();
          } catch (_error) {
            return;
          }
        }

        try {
          if (jobId) {
            await getJob({ jobId });
            await getCandidates({ jobId, itemsPerPage: 0 });
            await getClients({ jobId });
          } else {
            await getContract({ contractId });
          }
        } catch (_error) {
          return;
        }

        await fetchUser();
        initTable();

        const {
          customer: { companyName },
          job: { title },
          user: { type: userType, email },
        } = getStoreState();

        if (companyName && title && userType) {
          const accessType = !jobId ? 'prospetto' : 'cliente';

          const eventAction = `Accesso ${accessType}`;

          GTM.trackGTMEvent({
            action: eventAction,
            label: `${companyName} - ${jobId} - ${title}`,
          });

          GTM.trackGA4Event(eventAction, companyName, `${jobId} - ${title}`);

          Sentry.setUser({ userType, email });
        }
      } finally {
        actions.setInitialized(true);
      }
    },
  ),

  // actions
  setInitialized: action((state, payload) => {
    state.initialized = payload;
  }),

  setAuthenticationError: action((state, payload) => {
    state.authenticationError = payload;
  }),

  setCurrentRoute: action((state, payload) => {
    state.currentRoute = payload;
  }),

  setCurrentStatus: action((state, payload) => {
    state.currentStatus = payload;
  }),

  setModalOpened: action((state, payload) => {
    state.isModalOpened = payload;
  }),
  setModalId: action((state, payload) => {
    state.modalId = payload;
  }),

  setAppError: action((state, error) => {
    state.appError = error;
  }),

  updateDocumentTitle: thunk(
    async (actions, payload, { getState, dispatch }) => {
      const {
        currentRoute,
        job: { data: job },
        contract: { data: contract },
      } = getState();
      const step = i18next.t(`general.document_title.${currentRoute}`);

      document.title = `${step} - ${job?.title ?? contract?.title} - Reverse`;
    },
  ),
  changeLanguage: thunk(
    async (actions, selectedLanguage, { getStoreActions }) => {
      try {
        const {
          session: { authFetch },
        } = getStoreActions();
        await authFetch({
          endpoint: `/v1/auth/users/me/locale/${selectedLanguage}`,
          requestOptions: { method: 'PUT' },
        });
        window.location.reload();
      } catch (error) {
        await utilityChangeLocale(error.locale);
      }
    },
  ),
  // models
  customer: ModelCustomer,
  dealEntity: dealEntityStore,
  user: ModelUser,
  session,
  showcaseTable,
  job: jobStore,
  candidates: candidatesStore,
  contract: contractStore,
};

export const selectorRootActions = actions => actions;
export const selectorRootState = state => state;
export const selectorSetModalOpened = actions => actions.setModalOpened;
export const selectorSetModalId = actions => actions.setModalId;
